import { Link } from "@eworkit/iris";
import React, { FunctionComponent, memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { DialogDisclosure } from "reakit/Dialog";

import { Header } from "@/components/Header";
import { Text } from "@/components/Text";
import { IConsultantProfile } from "@/data/consultant-profiles";
import { IJobRequest } from "@/data/job-requests";
import { JobRequest } from "@/data/job-requests/types/JobRequest";
import { formatFullName } from "@/data/persons";
import { Flex, IFlexProps } from "@/eworkitComponents";
import { Avatar } from "@/views/components/Person/Avatar";
import { ApplyModal } from "@/views/job-applications/apply/ApplyModal";
import { useApplyModalState } from "@/views/job-applications/apply/useApplyModalState";

interface IJobRequestMatchingConsultantRecordProps extends Partial<IFlexProps> {
  jobRequest: JobRequest;
  profile: IConsultantProfile;
  onModalClosed: () => void;
}

export const JobRequestMatchingConsultantRecord: FunctionComponent<IJobRequestMatchingConsultantRecordProps> = memo(
  ({ jobRequest, profile, onModalClosed }) => {
    const { t } = useTranslation("session");

    const applyModalState = useApplyModalState(jobRequest as IJobRequest);

    const handleModalClosed = useCallback(() => {
      onModalClosed();
    }, [onModalClosed]);

    return (
      <Flex padding={["md", false]} justify="flex-start">
        <Avatar
          firstName={profile.firstName}
          lastName={profile.lastName}
          avatar={profile.avatar}
          size="xl"
          testName="avatarro"
          hjSuppress
        />
        <Flex direction="column" grow={1} padding={[false, "sm"]}>
          <Header size="sm" weight="bold">
            {formatFullName(profile)}
          </Header>
          <Text size="md" text={profile?.position?.name || ""} />
        </Flex>
        <Flex align="flex-start">
          <Link as={DialogDisclosure} {...applyModalState}>
            {t("JOB_REQUESTS.DETAIL.BUTTON_APPLY")}
          </Link>

          {applyModalState.visible && (
            <ApplyModal
              dialogState={applyModalState}
              jobRequest={jobRequest}
              initialProfile={profile}
              onClosed={handleModalClosed}
            />
          )}
        </Flex>
      </Flex>
    );
  },
);
